import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Search, Download, RefreshCw } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { toast, ToastContainer } from "react-toastify";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import axios from "axios";
import { exportToExcel } from "Common/Components/Excel";
import { getRoleId } from "Common/Components/getRoleId";
import { useNavigate } from "react-router-dom";

// ==================== تایپ‌ها ====================
interface ReportRow {
    row_type: string;
    label: string;
    voucher_bag_count: number;
    driver_discharge_bag_count: number;
    inspector_registration_bag_count: number;
    driver_not_discharged_bag_count: number;
    inspector_not_registered_bag_count: number;
}

interface ReportResponse {
    filters: {
        distribution_period_code: string;
        distribution_stage_code: string;
        voucher_to_discharge_days: number;
        discharge_to_inspection_days: number;
    };
    rows: ReportRow[];
}

interface DistributionPeriod {
    distribution_period_code: string;
    distribution_period: string;
}

interface DistributionStage {
    distribution_stage_code: string;
    distribution_stage: string;
}

// ==================== نقش‌های مجاز (مطابق مستندات) ====================
const ALLOWED_ROLES = [0, 1, 3, 5, 6]; // superadmin, admin, ai_observer, province_manager, city_manager

const ReportsDecadePage: React.FC = () => {
    const navigate = useNavigate();
    const userRole = getRoleId(localStorage.getItem("app_havir")) || 50;

    // ========== همه هوک‌ها در بالاترین سطح (قبل از هر شرط) ==========
    const [distributionPeriods, setDistributionPeriods] = useState<DistributionPeriod[]>([]);
    const [distributionStages, setDistributionStages] = useState<DistributionStage[]>([]);
    const [selectedPeriodCode, setSelectedPeriodCode] = useState<string>("");
    const [selectedStageCode, setSelectedStageCode] = useState<string>("");
    const [voucherToDischarge, setVoucherToDischarge] = useState<number>(30);
    const [dischargeToInspection, setDischargeToInspection] = useState<number>(30);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(20);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [reportRows, setReportRows] = useState<ReportRow[]>([]);
    const [allRows, setAllRows] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const isFirstRun = useRef(true);

    // ========== بررسی دسترسی (بعد از تعریف همه هوک‌ها) ==========
    // const hasAccess = ALLOWED_ROLES.includes(userRole);
    // if (!hasAccess) {
    //     return (
    //         <div className="flex items-center justify-center h-64">
    //             <p className="text-red-500 text-lg">شما دسترسی به این صفحه ندارید</p>
    //         </div>
    //     );
    // }

    // ========== دریافت لیست دوره‌های توزیع ==========
    useEffect(() => {
        const fetchPeriods = async () => {
            try {
                const response = await axios.get<DistributionPeriod[]>(
                    "https://rasad-api.havirard.ir/dropdown/distribution-periods",
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                        },
                    }
                );
                setDistributionPeriods(response.data || []);
                if (response.data.length === 0) {
                    toast.info("هیچ دوره توزیعی یافت نشد");
                }
            } catch (err) {
                console.error("خطا در دریافت دوره‌ها:", err);
                toast.error("خطا در دریافت لیست دوره‌های توزیع");
            }
        };
        fetchPeriods();
    }, []);

    // ========== دریافت لیست مراحل توزیع بر اساس دوره انتخاب‌شده ==========
    useEffect(() => {
        if (!selectedPeriodCode) {
            setDistributionStages([]);
            setSelectedStageCode("");
            return;
        }

        const fetchStages = async () => {
            try {
                const response = await axios.get<DistributionStage[]>(
                    "https://rasad-api.havirard.ir/dropdown/distribution-stages",
                    {
                        params: {
                            distribution_period_code: selectedPeriodCode,
                        },
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                        },
                    }
                );
                setDistributionStages(response.data || []);
                // اگر مرحله قبلی انتخاب شده بود و دیگر در لیست نبود، پاکش کن
                if (selectedStageCode && !response.data.some(s => s.distribution_stage_code === selectedStageCode)) {
                    setSelectedStageCode("");
                }
                if (response.data.length === 0) {
                    toast.info("هیچ مرحله توزیعی برای این دوره یافت نشد");
                }
            } catch (err) {
                console.error("خطا در دریافت مراحل:", err);
                toast.error("خطا در دریافت لیست مراحل توزیع");
            }
        };
        fetchStages();
    }, [selectedPeriodCode]);

    // ========== تابع فراخوانی گزارش ==========
    const fetchReport = useCallback(async () => {
        if (!selectedPeriodCode || !selectedStageCode) {
            toast.warning("لطفاً دوره و مرحله توزیع را انتخاب کنید");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axios.get<ReportResponse>(
                "https://rasad-api.havirard.ir/monitoring/reports/flour-distribution-period",
                {
                    params: {
                        distribution_period_code: selectedPeriodCode,
                        distribution_stage_code: selectedStageCode,
                        voucher_to_discharge_days: voucherToDischarge,
                        discharge_to_inspection_days: dischargeToInspection,
                    },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                    },
                }
            );

            const rows = response.data.rows || [];
            setAllRows(rows);
            setCurrentPage(1);

            if (rows.length === 0) {
                toast.info("هیچ داده‌ای برای این دوره و مرحله یافت نشد");
            }
        } catch (err: any) {
            if (err.response?.status === 403) {
                toast.error("شما دسترسی به این گزارش را ندارید");
                navigate("/");
            } else {
                const errorMsg = err.response?.data?.detail || "خطا در دریافت گزارش دهه";
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } finally {
            setLoading(false);
        }
    }, [selectedPeriodCode, selectedStageCode, voucherToDischarge, dischargeToInspection, navigate]);

    // ========== بارگذاری خودکار در اولین رندر (اگر هر دو انتخاب شده باشند) ==========
    useEffect(() => {
        if (isFirstRun.current && selectedPeriodCode && selectedStageCode) {
            isFirstRun.current = false;
            fetchReport();
        }
    }, [selectedPeriodCode, selectedStageCode, fetchReport]);

    // ========== مدیریت خودکار reportRows با useEffect ==========
    useEffect(() => {
        const startIndex = (currentPage - 1) * selectedNumber;
        const endIndex = Math.min(startIndex + selectedNumber, allRows.length);
        setReportRows(allRows.slice(startIndex, endIndex));
        setTotalPages(Math.ceil(allRows.length / selectedNumber) || 1);
    }, [allRows, selectedNumber, currentPage]);

    // ========== هندلرها ==========
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleSizeChange = (value: number) => {
        setSelectedNumber(value);
        setCurrentPage(1);
    };

    const handleSearch = () => {
        fetchReport();
    };

    const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const code = e.target.value;
        setSelectedPeriodCode(code);
        setSelectedStageCode(""); // پاک کردن مرحله قبلی
    };

    const handleStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedStageCode(e.target.value);
    };

    // ========== ستون‌های جدول ==========
    const columns = useMemo(
        () => [
            {
                header: "عنوان ردیف",
                accessorKey: "label",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "حواله نانوا",
                accessorKey: "voucher_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "تعداد کیسه تخلیه شده   ",
                accessorKey: "driver_discharge_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "ثبت بازرس",
                accessorKey: "inspector_registration_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "عدم تخلیه راننده",
                accessorKey: "driver_not_discharged_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "عدم ثبت بازرس",
                accessorKey: "inspector_not_registered_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
        ],
        []
    );

    // ========== خروجی اکسل ==========
    const handleExportExcel = () => {
        if (allRows.length === 0) {
            toast.warning("داده‌ای برای خروجی وجود ندارد");
            return;
        }
        const exportData = allRows.map((row) => ({
            "عنوان ردیف": row.label,
            "تعداد کیسه حواله (X1)": row.voucher_bag_count,
            "تعداد کیسه تخلیه شده در پنل راننده (X2)": row.driver_discharge_bag_count,
            "تعداد کیسه ثبت شده توسط بازرس (X3)": row.inspector_registration_bag_count,
            "تعداد کیسه تخلیه نشده توسط راننده (X12)": row.driver_not_discharged_bag_count,
            "تعداد کیسه ثبت نشده توسط بازرس پس از تخلیه (X13)": row.inspector_not_registered_bag_count,
        }));
        exportToExcel(exportData, "FlourDistributionDecadeReport");
    };

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="گزارش دهه" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl />

            <div className="card">
                <div className="card-body">
                    {/* بخش فیلترها */}
                    <div className="flex flex-col gap-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                            {/* دراپ‌داون دوره توزیع */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    دوره توزیع *
                                </label>
                                <select
                                    value={selectedPeriodCode}
                                    onChange={handlePeriodChange}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                >
                                    <option value="">انتخاب دوره</option>
                                    {distributionPeriods.map((period) => (
                                        <option key={period.distribution_period_code} value={period.distribution_period_code}>
                                            {period.distribution_period}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* دراپ‌داون مرحله توزیع */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    مرحله توزیع *
                                </label>
                                <select
                                    value={selectedStageCode}
                                    onChange={handleStageChange}
                                    disabled={!selectedPeriodCode}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                >
                                    <option value="">{selectedPeriodCode ? "انتخاب مرحله" : "ابتدا دوره را انتخاب کنید"}</option>
                                    {distributionStages.map((stage) => (
                                        <option key={stage.distribution_stage_code} value={stage.distribution_stage_code}>
                                            {stage.distribution_stage}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* فاصله زمانی حواله تا تخلیه */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    فاصله زمانی حواله تا تخلیه
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={voucherToDischarge}
                                    onChange={(e) =>
                                        setVoucherToDischarge(Number(e.target.value) || 0)
                                    }
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                />
                            </div>

                            {/* فاصله زمانی تخلیه تا بازرسی */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    فاصله زمانی تخلیه تا بازرسی
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={dischargeToInspection}
                                    onChange={(e) =>
                                        setDischargeToInspection(Number(e.target.value) || 0)
                                    }
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* دکمه‌های عملیات */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <RefreshCw
                                    className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                    onClick={handleSearch}
                                />
                                <span className="text-sm font-medium">
                                    مجموع رکوردها:{" "}
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                        {allRows.length}
                                    </span>
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-3 items-center">
                                <TableDropDownSmall onSelect={handleSizeChange} />
                                <button
                                    onClick={handleSearch}
                                    disabled={!selectedPeriodCode || !selectedStageCode}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl ${!selectedPeriodCode || !selectedStageCode
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-blue-600 hover:bg-blue-700"
                                        }`}
                                >
                                    <Search size={16} /> جستجو
                                </button>
                                <button
                                    onClick={handleExportExcel}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50"
                                >
                                    <Download size={16} /> اکسل
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* نمایش محتوای اصلی */}
                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : error ? (
                        <LoadingCounter count={0} text={error} />
                    ) : allRows.length === 0 ? (
                        <div className="flex justify-center mx-auto mt-10 w-full">
                            <button
                                onClick={handleSearch}
                                disabled={!selectedPeriodCode || !selectedStageCode}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl ${!selectedPeriodCode || !selectedStageCode
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-600 hover:bg-green-700"
                                    }`}
                            >
                                <Search size={16} /> جستجو با مقادیر پیش فرض
                            </button>
                        </div>
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={reportRows}
                                customPageSize={reportRows.length}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            {totalPages > 1 && (
                                <PaginationTest
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default ReportsDecadePage;